import { StarIcon } from "@heroicons/react/24/solid";
import GallerySlider from "components/GallerySlider/GallerySlider";
import type { Hotel } from "containers/PageHome/type";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import { type FC, useState } from "react";
import { FaWallet } from "react-icons/fa";
import { SlLocationPin } from "react-icons/sl";
import { Link } from "react-router-dom";
import type { Root as RootProfile } from "services/get-profile";
import { Formatter } from '../../utils/formatters';

export interface StayCardProps {
  className?: string;
  data: Hotel;
  size?: "default" | "small";
  profile: RootProfile;
}

const StayCard: FC<StayCardProps> = ({
  size = "default",
  className = "",
  data,
  profile,
}) => {
  const { destination, checkInDate, numNights, guests, token, destinationName } = {
    ...useHotels(),
  };

  const rating = (points: number) => {
    const stars = [];
    for (let i = 0; i < points; i++) {
      stars.push(
        <StarIcon key={i} className="w-[14px] h-[14px] text-orange-500" />
      );
    }
    return stars;
  };

  const [guestsHotel] = useState(guests.adults + (guests.children ?? 0));
  const [numNightsHotel] = useState(numNights);

  const totalGuestsText =
    guestsHotel > 1 ? `${guestsHotel} Hóspedes` : "1 Hóspede";
  const nightsText =
    numNightsHotel > 1 ? `${numNightsHotel} Noites` : `1 Noite`;

  const handleRedirectHotelDetails = () => {
    localStorage.removeItem("hotelSelected");
    const inputsSearchHotels = {
      hotelId: data?.id,
      destination,
      checkInDate,
      numNights,
      guests,
    };
    localStorage.setItem("hotelSelected", JSON.stringify(inputsSearchHotels));
  };

  let link = "";

  if (guests.agesChildren) {
    link = `/hotel/${data && data.destination.id}/${
      data && data.id
    }?dest=${destinationName}&checkin=${checkInDate}&token=${token}&adults=${guests.adults}&children=${
      guests.children
    }&nights=${numNights}&ages_children=${String(guests.agesChildren)}`;
  } else {
    link = `/hotel/${data && data.destination.id}/${
      data && data.id
    }?dest=${destinationName}&checkin=${checkInDate}&token=${token}&adults=${guests.adults}&children=${
      guests.children
    }&nights=${numNights}`;
  }

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full">
        {data.photos && (
          <GallerySlider
            uniqueID={`StayCard_${data.id}`}
            ratioClass="aspect-w-4 aspect-h-3 "
            galleryImgs={
              data.photos.length >= 1 ? data.photos : [data.thumb_url]
            }
            href={link}
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div
        className={
          size === "default"
            ? "p-4 space-y-4 border border-neutral-100 dark:border-none"
            : "p-3 space-y-4 border border-neutral-100 dark:border-none"
        }
      >
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <h2
              className={` font-medium capitalize ${
                size === "default" ? "text-lg" : "text-base"
              }`}
            >
              <span className="line-clamp-1">{data.name}</span>
            </h2>
          </div>
          <div className="flex items-center text-neutral-500 dark:text-neutral-400 text-sm space-x-2">
            <div className="w-1/10">
              <SlLocationPin size={14} />
            </div>
            <span className="truncate text-ellipsis overflow-hidden w-6/10">
              {data.address}
            </span>
            <span>|</span>
            <span className="flex justify-items-end w-3/10">
              {!!data?.category && rating(Number.parseInt(data.category, 10))}
            </span>
          </div>
        </div>
        {profile.wallet.balance > 0 || profile.subscription?.active ? (
          <div className="relative h-16">
            <div className="absolute right-0">
              <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal ">
                {nightsText}, {totalGuestsText}
              </span>
            </div>
            <span className="absolute right-0 top-6 text-md text-black-500 dark:text-white-500 font-bold ">
              {
                <p className="flex justify-end items-center">
                  <FaWallet size={20} className="mr-2" />{" "}
                  <span
                    className={`
                    ${
                      profile.wallet.balance === 0 &&
                      profile.subscription?.active
                        ? "text-xs sm:text-base md:text-[0.8rem]"
                        : ""
                    } 
                  `}
                  >
                    {Formatter.prettyPriceHotel(
                      data?.price_room?.price,
                      profile
                    )}
                  </span>
                </p>
              }
            </span>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <Link
              to={"/plans"}
              className="bg-[--blue] hover:bg-[--blue-dark] text-sm mx-auto font-semibold text-white rounded-full py-2 px-4 transition-colors duration-500 ease-in-out"
            >
              Faça sua assinatura
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative group flex justify-end">
      {profile.wallet.balance > 0
        ? data.cashback.value > 0 && (
            <div className="absolute z-20 w-[200px] group-hover:animate-pulse mt-2 mx-2 lg:mt-[-35px] lg:mr-[-25px] bg-[#F97316] p-3 rounded-lg shadow-xl shadow-[#00000080] lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-500">
              <span className="font-normal text-[14px] leading-6 text-center text-white">
                Receba <strong>{`${data.cashback.value} ViroCash`}</strong> de
                volta com essa reserva!
              </span>
            </div>
          )
        : null}
      <div
        className={`nc-StayCard relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden will-change-transform hover:shadow-xl transition-shadow ${className}`}
        data-nc-id="StayCard"
        onClick={() => handleRedirectHotelDetails()}
      >
        {renderSliderGallery()}
        <a href={link} target="_blank" rel="noreferrer">
          {renderContent()}
        </a>
      </div>
    </div>
  );
};

export default StayCard;
